/**
 * Dynamic metadata for the public SPA.
 *
 * Google can process these tags after rendering JavaScript. Server-side or
 * static rendering remains the preferred long-term option for faster indexing.
 */

import { useEffect } from 'react';

interface SEOHeadProps {
    title: string;
    description?: string;
    image?: string;
    type?: 'website' | 'article';
    canonicalPath?: string;
    structuredData?: Record<string, unknown>;
}

export function SEOHead({
    title,
    description,
    image,
    type = 'website',
    canonicalPath,
    structuredData,
}: SEOHeadProps) {
    useEffect(() => {
        const fullTitle = title ? `${title} | Mordeczka.eu` : 'Mordeczka.eu';
        const canonicalUrl = new URL(
            canonicalPath || window.location.pathname,
            'https://mordeczka.eu',
        ).toString();
        document.title = fullTitle;

        const setMeta = (name: string, content: string, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attr, name);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        const removeMeta = (name: string, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            document.querySelector(`meta[${attr}="${name}"]`)?.remove();
        };

        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;

        setMeta('robots', 'index,follow,max-image-preview:large');
        setMeta('og:title', fullTitle, true);
        setMeta('og:type', type, true);
        setMeta('og:site_name', 'Mordeczka.eu', true);
        setMeta('og:url', canonicalUrl, true);
        setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
        setMeta('twitter:title', fullTitle);

        if (description) {
            setMeta('description', description);
            setMeta('og:description', description, true);
            setMeta('twitter:description', description);
        }
        if (image) {
            setMeta('og:image', image, true);
            setMeta('twitter:image', image);
        } else {
            removeMeta('og:image', true);
            removeMeta('twitter:image');
        }

        const existingJsonLd = document.querySelector('script[data-seo-jsonld]');
        existingJsonLd?.remove();
        if (structuredData) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.seoJsonld = 'true';
            script.text = JSON.stringify(structuredData);
            document.head.appendChild(script);
        }

        return () => {
            document.querySelector('script[data-seo-jsonld]')?.remove();
        };
    }, [title, description, image, type, canonicalPath, structuredData]);

    return null;
}

export default SEOHead;
